import styled from "styled-components";
import { Field, Form, Formik } from "formik";
import {
  Dialog,
  DialogType,
  DialogFooter,
  PrimaryButton,
  DefaultButton,
  TextField,
  Label,
  Checkbox,
} from "@fluentui/react";
import React, { useContext, useState } from "react";
import { ColumnOptionsContext } from "./ColumnOptions";

type Props = {
  close: () => void;
  viewId: string | null;
};

export const ViewDialog = ({ close, viewId }: Props) => {
  const {
    views,
    selectedView,
    onCreateView,
    onUpdateView,
    isViewLoading,
    strings,
  } = useContext(ColumnOptionsContext);
  const [didTrySubmit, setDidTrySubmit] = useState(false);

  const isEdit = viewId !== null;
  const isLoading = isViewLoading;

  const onSubmit = (data: any) => {
    const trimmedName = data.name.trim();
    const trimmedDescription = data.description.trim();
    if (!isEdit) {
      onCreateView({
        name: trimmedName,
        description: trimmedDescription,
        isDefault: data.isDefault,
      });
    } else {
      onUpdateView({
        name: trimmedName,
        description: trimmedDescription,
        isDefault: data.isDefault,
      });
    }
    close();
  };

  return (
    <Dialog
      hidden={false}
      onDismiss={close}
      dialogContentProps={{
        type: DialogType.normal,
        title: isEdit
          ? strings.editViewDialogLabel
          : strings.createViewDialogLabel,
        showCloseButton: true,
      }}
      modalProps={{
        isBlocking: true,
      }}
      minWidth={400}
    >
      <Formik
        initialValues={
          isEdit && selectedView
            ? {
                name: selectedView.name,
                description: selectedView.description,
                isDefault: selectedView.isDefault,
              }
            : {
                name: "",
                description: "",
                isDefault: false,
              }
        }
        onSubmit={onSubmit}
        validateOnChange={didTrySubmit}
        validateOnBlur={didTrySubmit}
      >
        {({ submitForm }) => (
          <Form>
            <Field
              name="name"
              validate={(value: string) => {
                if (value.trim() === "") return strings.requiredErrorMessage;
                let doesExist = !!views.find((v) => v.name === value.trim());
                if (isEdit) {
                  doesExist = !!views
                    .filter((v) => v.id !== selectedView?.id)
                    .find((v) => v.name === value.trim());
                }
                if (doesExist) return strings.viewExistsErrorMessage;
                return undefined;
              }}
            >
              {({
                field: { onChange, value, ...field },
                meta: { error },
              }: any) => (
                <FieldContainer>
                  <Label required>{strings.viewDialogNameFieldLabel}</Label>
                  <TextField
                    autoFocus
                    maxLength={128}
                    value={value}
                    onChange={(_, newValue) => {
                      onChange({
                        target: { name: field.name, value: newValue ?? "" },
                      });
                    }}
                    errorMessage={error}
                    {...field}
                  />
                </FieldContainer>
              )}
            </Field>
            <Field name="description">
              {({
                field: { onChange, value, ...field },
                meta: { error },
              }: any) => (
                <FieldContainer>
                  <Label>{strings.viewDialogDescriptionFieldLabel}</Label>
                  <TextField
                    maxLength={128}
                    value={value}
                    onChange={(_, newValue) => {
                      onChange({
                        target: { name: field.name, value: newValue ?? "" },
                      });
                    }}
                    errorMessage={error}
                    {...field}
                  />
                </FieldContainer>
              )}
            </Field>
            <Field name="isDefault" type="checkbox">
              {({
                field: { value, ...field },
                form: { setFieldValue },
              }: any) => (
                <FieldContainer>
                  <Checkbox
                    label={strings.viewDialogFavoriteFieldLabel}
                    checked={value}
                    onChange={(_, checked) =>
                      setFieldValue(field.name, checked)
                    }
                  />
                </FieldContainer>
              )}
            </Field>
            <DialogFooter>
              <DefaultButton onClick={close} disabled={isLoading}>
                Cancel
              </DefaultButton>
              <PrimaryButton
                onClick={() => {
                  setDidTrySubmit(true);
                  submitForm();
                }}
                disabled={isLoading}
              >
                {strings.viewDialogSaveButtonLabel}
              </PrimaryButton>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const FieldContainer = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
